import { ExternalLink, Edit2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImageUpload } from "./ImageUpload";
import {
  useUploadCameraPlanImage,
  useUpdateCameraPlan,
} from "@/hooks/useCameraPlans";
import {
  getDraftFieldKeyDownHandlers,
  useDraftField,
} from "@/hooks/useDraftField";
import { CameraPlanImageMap } from "./CameraPlanImageMap";
import type { Camera, CameraPlan } from "@/types/camera-plans";

interface CameraMapViewProps {
  plan: CameraPlan;
  cameras: Camera[];
  cameraPlanId: string;
  projectId: string;
  selectedCameraId: string | null;
  onSelectCamera: (id: string | null) => void;
}

/** Converts a share/view URL to an embeddable URL for known providers. */
function toEmbedUrl(url: string): string {
  // Figma: convert share links to embed
  if (url.includes("figma.com/") && !url.includes("/embed")) {
    return `https://www.figma.com/embed?embed_host=production-hub&url=${encodeURIComponent(url)}`;
  }
  // Google Maps: convert share links to embed
  if (
    url.includes("google.com/maps") ||
    url.includes("google.ch/maps") ||
    url.includes("maps.google.")
  ) {
    if (!url.includes("/embed")) {
      // For place/search/directions URLs, use the embed API format
      return url.replace("/maps/", "/maps/embed/v1/place?q=");
    }
  }
  return url;
}

export function CameraMapView({
  plan,
  cameras,
  cameraPlanId,
  projectId,
  selectedCameraId,
  onSelectCamera,
}: CameraMapViewProps) {
  const uploadImage = useUploadCameraPlanImage();
  const updatePlan = useUpdateCameraPlan();
  const embedUrlDraft = useDraftField<string, string | null>({
    value: plan.embedURL,
    fromShared: (value) => value ?? "",
    toShared: (draft) => draft.trim() || null,
    onCommit: (embedUrl) =>
      updatePlan.mutate({
        id: cameraPlanId,
        projectId,
        updates: { embedURL: embedUrl },
      }),
  });

  if (plan.mapType === "embed") {
    if (!plan.embedURL && !embedUrlDraft.isEditing) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-12">
          <ExternalLink className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No embed URL set yet.</p>
          <Button
            offlineEdit
            variant="outline"
            size="sm"
            onClick={embedUrlDraft.start}
          >
            Add Embed URL
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          {embedUrlDraft.isEditing ? (
            <>
              <Input
                value={embedUrlDraft.draft}
                onChange={(e) => embedUrlDraft.setDraft(e.target.value)}
                placeholder="Paste Figma, Google Maps, or any embed URL..."
                className="flex-1"
                autoFocus
                onKeyDown={getDraftFieldKeyDownHandlers(embedUrlDraft)}
              />
              <Button
                offlineEdit
                variant="ghost"
                size="icon"
                onClick={() => void embedUrlDraft.commit()}
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                offlineEdit
                variant="ghost"
                size="icon"
                onClick={embedUrlDraft.cancel}
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground truncate flex-1">
                {plan.embedURL}
              </p>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={embedUrlDraft.start}
              >
                <Edit2 className="h-3.5 w-3.5" />
              </Button>
              <a
                href={plan.embedURL!}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </a>
            </>
          )}
        </div>
        {plan.embedURL && !embedUrlDraft.isEditing && (
          <div className="rounded-lg border overflow-hidden bg-muted">
            <iframe
              src={toEmbedUrl(plan.embedURL)}
              className="w-full h-[500px] border-0"
              allowFullScreen
              title={`${plan.name} embed`}
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            />
          </div>
        )}
      </div>
    );
  }

  // Image type
  if (!plan.imageURL) {
    return (
      <ImageUpload
        onUpload={(file) =>
          uploadImage.mutate({ cameraPlanId, projectId, file })
        }
        isUploading={uploadImage.isPending}
        className="h-[300px]"
      />
    );
  }

  return (
    <CameraPlanImageMap
      imageUrl={plan.imageURL}
      cameras={cameras}
      cameraPlanId={cameraPlanId}
      selectedCameraId={selectedCameraId}
      onSelectCamera={onSelectCamera}
      className="min-h-[500px]"
    />
  );
}
